/**
 * prescriptions.service.ts
 *
 * Fetches the server-rendered context the prescriptions tab needs, aligned
 * with PrescriptionsController::context (API_v5). The context payload is a
 * discriminated union on `provider`: TreatRx accounts get the full legacy
 * context (the TreatRx middleware calls themselves live in treatrx.client.ts
 * and run direct from the browser); DAW (ScriptSure) accounts get a slim
 * payload plus the session/events calls, the practice-level window open the
 * Prescribing page uses, and the medication-history consent calls below.
 */
import axios from 'axios';
import { apiClient } from './apiClient';
import type { ApiResponse } from '@/types/api.types';

export interface PrescriptionProvider {
  ayvaDoctorID: string;
  name: string;
}

export interface PrescriptionPatient {
  patientID: number;
  ftp_patientID: number;
  aaPatientID: number;
  accountID: number;
  encReference: string;
  dateOfBirth: string;
  firstName: string;
  lastName: string;
  middleName: string;
  mrn: string;
  gender: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  mobileNumber: string;
  email: string;
  weight: string;
  language: string;
}

export interface TreatRxPrescriptionsContext {
  provider: 'treatrx';
  ayvaClientID: string;
  ayvaPatientID: string | null;
  providers: PrescriptionProvider[];
  defaultAyvaDoctorID: string;
  /**
   * The signed-in user's own TreatRx provider row, or null if they have none.
   * Only this provider can be authorized from here — TreatRx consent happens in
   * the clicker's own Bravado account.
   */
  myAyvaDoctorID: string | null;
  patient: PrescriptionPatient;
}

/**
 * Presence hints for the fields whose absence blocks a push to ScriptSure.
 * They do not cover the per-field length caps, so passing every hint means
 * likely-pushable, not guaranteed — the session endpoint is the real gate.
 */
export interface DawContextPatient {
  patientID: number;
  firstName: string;
  lastName: string;
  hasDob: boolean;
  /** Always true: ScriptSure accepts M, F and U, so gender never blocks. */
  hasGender: boolean;
  hasAddressLine1: boolean;
  hasCity: boolean;
  hasState: boolean;
  hasZip: boolean;
  /** ScriptSure rejects a patient whose home, work and cell are all empty. */
  hasPhone: boolean;
}

export interface DawPrescriptionsContext {
  provider: 'daw';
  configured: boolean;
  patient: DawContextPatient;
  /**
   * The practice is moving from TreatRx and still has TreatRx on and set up,
   * so the tab offers TreatRx beside ScriptSure. False when the server sent
   * no flag.
   */
  treatRxAvailable: boolean;
}

export type PrescriptionsContext = TreatRxPrescriptionsContext | DawPrescriptionsContext;

export interface PrescriptionGrantFlow {
  handle: string;
  ayvaDoctorID: string;
  expiresInSeconds: number;
}

export function getPrescriptionsContext(
  patientId: number,
  ayvaDoctorID?: string,
): Promise<PrescriptionsContext> {
  return apiClient
    .get<ApiResponse<PrescriptionsContext>>(`/patients/${patientId}/prescriptions/context`, {
      params: ayvaDoctorID ? { ayvaDoctorID } : undefined,
    })
    .then((r) => {
      const raw = r.data.data as PrescriptionsContext & { provider?: string };
      if (raw?.provider === 'daw') {
        return { ...raw, treatRxAvailable: raw.treatRxAvailable === true } as DawPrescriptionsContext;
      }
      // Older/stale payloads may lack the discriminator — they are always the
      // legacy TreatRx shape, so default the provider rather than crash.
      return { ...raw, provider: 'treatrx' } as TreatRxPrescriptionsContext;
    });
}

/**
 * The TreatRx answer for a practice whose default answer is ScriptSure with
 * `treatRxAvailable`. The server refuses it (422 TREATRX_NOT_AVAILABLE) once
 * TreatRx is turned off for the practice.
 */
export function getTreatRxPrescriptionsContext(
  patientId: number,
): Promise<TreatRxPrescriptionsContext> {
  return apiClient
    .get<ApiResponse<TreatRxPrescriptionsContext>>(`/patients/${patientId}/prescriptions/context`, {
      params: { provider: 'treatrx' },
    })
    .then((r) => ({ ...r.data.data, provider: 'treatrx' }) as TreatRxPrescriptionsContext);
}

// ─── DAW (ScriptSure) session + activity ─────────────────────────────────────

export interface DawPrescriber {
  prescriberId: number;
  name: string;
}

export interface DawPractice {
  practiceId: number;
  name: string;
  prescribers: DawPrescriber[];
}

export interface DawSessionSelection {
  needsSelection: true;
  practices: DawPractice[];
  currentPracticeId: number | null;
  currentPrescriberId: number | null;
}

export interface DawSessionReady {
  needsSelection: false;
  iframeUrl: string;
  dawPatientId: number;
  practiceId: number;
  prescriberId: number;
  expiresAtIso: string;
}

export type DawSessionResult = DawSessionSelection | DawSessionReady;

/**
 * The ScriptSure screens a patient's Prescriptions tab opens. The server
 * builds each one's address from this fixed list; the client never composes a
 * ScriptSure URL itself.
 */
export type DawPatientWindow =
  | 'chart'
  | 'patient_messages'
  | 'medication_history'
  | 'prescribed_history';

/** The practice-level ScriptSure screens the Prescribing page opens. */
export type DawWorkspaceWindow = 'messages' | 'pending' | 'reports' | 'audit_log' | 'app';

export type DawWindowKind = DawPatientWindow | DawWorkspaceWindow;

export interface DawSessionBody {
  practiceId?: number;
  prescriberId?: number;
  /** Absent means the chart, so a body without it opens what it always did. */
  window?: DawPatientWindow;
}

export function createDawSession(
  patientId: number,
  body?: DawSessionBody,
): Promise<DawSessionResult> {
  return apiClient
    .post<ApiResponse<DawSessionResult>>(
      `/patients/${patientId}/prescriptions/daw/session`,
      body ?? {},
    )
    .then((r) => r.data.data);
}

export interface DawWorkspaceBody {
  window: DawWorkspaceWindow;
  practiceId?: number;
  prescriberId?: number;
}

/** A practice-level window has no patient, so it carries no ScriptSure patient id. */
export interface DawWorkspaceReady {
  needsSelection: false;
  iframeUrl: string;
  practiceId: number;
  prescriberId: number;
  expiresAtIso: string;
}

export type DawWorkspaceResult = DawSessionSelection | DawWorkspaceReady;

/**
 * Open one practice-level ScriptSure screen. Called once per window, when the
 * user opens it: every open re-scopes the user's one shared ScriptSure
 * session, so an address issued earlier can already be running under another
 * practice or prescriber (plan KTD4).
 */
export function openDawWorkspace(body: DawWorkspaceBody): Promise<DawWorkspaceResult> {
  return apiClient
    .post<ApiResponse<DawWorkspaceResult>>('/prescriptions/daw/workspace', body)
    .then((r) => r.data.data);
}

// ─── DAW (ScriptSure) prescription list + pharmacy messages ──────────────────

/** One status a prescription passed through. `label` is ready to show. */
export interface DawPrescriptionStatus {
  label: string;
  /** ScriptSure's status code; 0 when it sent none. */
  code: number;
  /** When ScriptSure recorded the status (ISO 8601), or null when it did not say. */
  at: string | null;
}

/**
 * How the latest send went, from the pharmacy network's own answer:
 * 'status' (it went through), 'verify' (the pharmacy confirmed it) or
 * 'error' (it did not go through).
 */
export type DawSendOutcomeKind = 'status' | 'verify' | 'error';

export interface DawSendOutcome {
  kind: DawSendOutcomeKind;
  at: string | null;
  /** The pharmacy message that reported it. */
  messageId: number;
}

/** One stored pharmacy message, as the list names it. */
export interface DawPharmacyMessageSummary {
  /** The stored message's id, for getDawMessage. */
  id: number;
  /** ScriptSure's message type (NewRx, RxRenewalRequest, Status …); '' when it could not be read. */
  type: string;
  /** '' when the type does not say. */
  direction: 'inbound' | 'outbound' | '';
  /** When it was sent, else when it arrived (ISO 8601). */
  at: string | null;
  /** The message carries signature information. Reported, never checked. */
  hasSignature: boolean;
}

export interface DawPrescriptionEntry {
  key: string;
  /** ScriptSure's prescription id, or null when only pharmacy messages name it. */
  prescriptionId: number | null;
  medication: string;
  prescriber: string;
  /** The newest status, or null when none has arrived. */
  status: DawPrescriptionStatus | null;
  /** Newest first. */
  history: DawPrescriptionStatus[];
  outcome: DawSendOutcome | null;
  /** The latest send outcome is an error. */
  hasError: boolean;
  /** Oldest first. */
  messages: DawPharmacyMessageSummary[];
  lastActivityAt: string | null;
}

export interface DawPrescriptionList {
  /** Most recently active first. */
  prescriptions: DawPrescriptionEntry[];
  /** Pharmacy messages that belong to no prescription the list can name, newest first. */
  otherMessages: DawPharmacyMessageSummary[];
}

/** The prefix every patient's prescription list is cached under. */
export const DAW_PRESCRIPTION_LISTS = ['daw-prescriptions'] as const;

/**
 * The query key one patient's list lives under. The tab's Refresh button and
 * ScriptSure's '200' signal refresh it; the Prescribing page refreshes every
 * patient's through DAW_PRESCRIPTION_LISTS.
 */
export function dawPrescriptionListKey(patientId: number) {
  return [...DAW_PRESCRIPTION_LISTS, patientId] as const;
}

export function getDawPrescriptions(patientId: number): Promise<DawPrescriptionList> {
  return apiClient
    .get<ApiResponse<DawPrescriptionList>>(`/patients/${patientId}/prescriptions/daw/prescriptions`)
    .then((r) => ({
      prescriptions: r.data.data?.prescriptions ?? [],
      otherMessages: r.data.data?.otherMessages ?? [],
    }));
}

export interface DawSignatureElement {
  name: string;
  /** Where in the message it sits, e.g. 'Message/Header/DigitalSignature'. */
  path: string;
}

export interface DawPharmacyMessage extends DawPharmacyMessageSummary {
  /**
   * The message as received, with the header's network sign-in values
   * masked by the server. Plain text: never rendered as markup.
   */
  body: string;
  signature: { found: boolean; elements: DawSignatureElement[] };
}

export function getDawMessage(patientId: number, messageId: number): Promise<DawPharmacyMessage> {
  return apiClient
    .get<ApiResponse<{ message: DawPharmacyMessage }>>(
      `/patients/${patientId}/prescriptions/daw/messages/${messageId}`,
    )
    .then((r) => r.data.data.message);
}

// ─── DAW (ScriptSure) medication-history consent ─────────────────────────────

export interface MedicationHistoryConsent {
  /**
   * The patient agreed to let the practice download their medication history
   * from pharmacies and insurers. False when nothing is on file, and also when
   * the history could not be read: the server fails closed.
   */
  consented: boolean;
  /** How it was obtained, in plain words. Null when nothing is on file. */
  source: string | null;
  /** Pacific wall clock, 'Y-m-d H:i:s' — the same shape as the WhatsApp consent's. */
  recordedAt: string | null;
  /** The staff member who recorded it, or null when no person did. */
  recordedBy: string | null;
}

export function getMedicationHistoryConsent(patientId: number): Promise<MedicationHistoryConsent> {
  return apiClient
    .get<ApiResponse<MedicationHistoryConsent>>(
      `/patients/${patientId}/prescriptions/daw/medication-history-consent`,
    )
    .then((r) => r.data.data);
}

/**
 * Record that the patient agreed, or withdraw it with `consented: false`.
 *
 * The body field is `consented`, not the WhatsApp endpoint's `optedIn`: the
 * server refuses a write without it rather than reading a missing value as a
 * withdrawal. Recording agreement requires `confirmed`, the staff member's word
 * that they asked the patient; withdrawing does not.
 */
export function setMedicationHistoryConsent(
  patientId: number,
  consented: boolean,
  confirmed = false,
): Promise<MedicationHistoryConsent> {
  return apiClient
    .post<ApiResponse<MedicationHistoryConsent>>(
      `/patients/${patientId}/prescriptions/daw/medication-history-consent`,
      { consented, confirmed },
    )
    .then((r) => r.data.data);
}

/** The `error` member of a DAW failure response: the nested 422 object, the flat 403 string, or null. */
function parseDawError(
  err: unknown
): { code?: string; details?: { missing?: unknown } } | string | null {
  if (!axios.isAxiosError(err)) return null;
  const data = err.response?.data as
    | { error?: { code?: string; details?: { missing?: unknown } } | string }
    | undefined;
  return data?.error ?? null;
}

/**
 * Pull the stable domain-error code out of a failed DAW call. Handles both
 * envelope shapes the backend produces:
 *   - 422 `{error: {code: 'DAW_...', message, details?}}`
 *   - 403 flat `{success: false, error: 'MODULE_DISABLED', module, message}`
 */
export function getDawErrorCode(err: unknown): string | null {
  const error = parseDawError(err);
  if (error == null) return null;
  if (typeof error === 'string') return error;
  return error.code ?? null;
}

/** The `details.missing` list from a DAW_PATIENT_INCOMPLETE failure. */
export function getDawMissingFields(err: unknown): string[] {
  const error = parseDawError(err);
  if (typeof error !== 'object' || error == null) return [];
  const missing = error.details?.missing;
  return Array.isArray(missing) ? missing.map(String) : [];
}

/**
 * Mint a one-time handle for the TreatRx OAuth grant.
 *
 * `returnPath` is a path on THIS origin only — the server pairs it with the
 * request's Origin header and rejects anything else, so the legacy host can
 * never be turned into an open redirect.
 */
export function createPrescriptionGrantFlow(
  patientId: number,
  returnPath: string,
): Promise<PrescriptionGrantFlow> {
  return apiClient
    .post<ApiResponse<PrescriptionGrantFlow>>(
      `/patients/${patientId}/prescriptions/grant-flow`,
      { returnPath },
    )
    .then((r) => r.data.data);
}
